/**
 * Client mirror of functions/src/cardPoLinkedCharges.ts — append/dedupe +
 * shared-charge attribution for purchasedAmount.
 */

import { roundMoney, type LinkedCardChargeLite } from './cardPoAutoPurchase';

export function normalizeLinkedExpenseIds(
  qbExpenseIds?: unknown,
  qbExpenseId?: unknown,
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (raw: unknown) => {
    const id = typeof raw === 'string' ? raw.trim() : '';
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(id);
  };
  if (Array.isArray(qbExpenseIds)) {
    for (const value of qbExpenseIds) push(value);
  }
  push(qbExpenseId);
  return out;
}

export function appendLinkedExpenseId(existing: string[], expenseId: string): string[] {
  const id = expenseId.trim();
  if (!id) return normalizeLinkedExpenseIds(existing);
  if (existing.some(row => row === id)) return [...existing];
  return [...existing, id];
}

export function removeLinkedExpenseId(existing: string[], expenseId: string): string[] {
  const id = expenseId.trim();
  if (!id) return [...existing];
  return existing.filter(row => row !== id);
}

export function sumLinkedChargeAmounts(charges: LinkedCardChargeLite[]): number {
  return roundMoney(charges.reduce((sum, row) => sum + (Number(row.totalAmount) || 0), 0));
}

export type PurchaseOrderSplitLite = {
  purchaseRequisitionId?: string | null;
  amount?: number | null;
  poNumber?: string | null;
};

export type ChargeAttributionMethod = 'exclusive' | 'split_allocation' | 'even_split';

export function evenSplitAmounts(total: number, poIds: string[]): Map<string, number> {
  const ids = Array.from(
    new Set(
      poIds
        .map(id => (typeof id === 'string' ? id.trim() : ''))
        .filter(Boolean),
    ),
  ).sort();
  const map = new Map<string, number>();
  if (!ids.length) return map;
  const cents = Math.round(roundMoney(total) * 100);
  const base = Math.floor(cents / ids.length);
  let rem = cents - base * ids.length;
  for (const id of ids) {
    const share = base + (rem > 0 ? 1 : 0);
    if (rem > 0) rem -= 1;
    map.set(id, share / 100);
  }
  return map;
}

export function resolveChargeAmountForPo(input: {
  purchaseRequisitionId: string;
  totalAmount: number;
  purchaseOrderSplits?: PurchaseOrderSplitLite[] | null;
  linkedPurchaseRequisitionIds?: string[] | null;
}): {
  amount: number;
  method: ChargeAttributionMethod;
  sharedWith: string[];
} {
  const poId = (input.purchaseRequisitionId || '').trim();
  const total = Number(input.totalAmount) || 0;
  const splits = Array.isArray(input.purchaseOrderSplits) ? input.purchaseOrderSplits : [];

  const alloc = splits.find(row => String(row?.purchaseRequisitionId || '').trim() === poId);
  if (alloc && alloc.amount != null && Number.isFinite(Number(alloc.amount))) {
    const sharedWith = Array.from(
      new Set(
        splits
          .map(row => String(row?.purchaseRequisitionId || '').trim())
          .filter(Boolean),
      ),
    ).sort();
    return {
      amount: roundMoney(Number(alloc.amount)),
      method: 'split_allocation',
      sharedWith: sharedWith.length ? sharedWith : [poId],
    };
  }

  const linked = Array.from(
    new Set(
      [
        ...(Array.isArray(input.linkedPurchaseRequisitionIds)
          ? input.linkedPurchaseRequisitionIds
          : []),
        poId,
        ...splits.map(row => String(row?.purchaseRequisitionId || '').trim()),
      ]
        .map(id => (typeof id === 'string' ? id.trim() : ''))
        .filter(Boolean),
    ),
  ).sort();

  if (linked.length > 1) {
    const shares = evenSplitAmounts(total, linked);
    return {
      amount: shares.get(poId) ?? 0,
      method: 'even_split',
      sharedWith: linked,
    };
  }

  return {
    amount: roundMoney(total),
    method: 'exclusive',
    sharedWith: poId ? [poId] : [],
  };
}

export type ChargeForPoAttribution = {
  id: string;
  totalAmount?: number | null;
  purchaseOrderSplits?: PurchaseOrderSplitLite[] | null;
};

export function attributeChargesToPurchaseOrder(input: {
  purchaseRequisitionId: string;
  charges: ChargeForPoAttribution[];
  expenseToPoIds?: Map<string, string[]> | Record<string, string[]>;
}): {
  total: number;
  attributions: Array<{
    expenseId: string;
    chargeTotal: number;
    amountForThisPo: number;
    method: ChargeAttributionMethod;
    sharedWithPoIds: string[];
  }>;
} {
  const poId = (input.purchaseRequisitionId || '').trim();
  const lookup = (expenseId: string): string[] => {
    if (!input.expenseToPoIds) return [poId];
    if (input.expenseToPoIds instanceof Map) {
      return input.expenseToPoIds.get(expenseId) || [poId];
    }
    return input.expenseToPoIds[expenseId] || [poId];
  };

  const attributions: Array<{
    expenseId: string;
    chargeTotal: number;
    amountForThisPo: number;
    method: ChargeAttributionMethod;
    sharedWithPoIds: string[];
  }> = [];
  let total = 0;

  for (const charge of input.charges) {
    const expenseId = String(charge.id || '').trim();
    if (!expenseId) continue;
    const chargeTotal = Number(charge.totalAmount) || 0;
    const resolved = resolveChargeAmountForPo({
      purchaseRequisitionId: poId,
      totalAmount: chargeTotal,
      purchaseOrderSplits: charge.purchaseOrderSplits,
      linkedPurchaseRequisitionIds: lookup(expenseId),
    });
    attributions.push({
      expenseId,
      chargeTotal: roundMoney(chargeTotal),
      amountForThisPo: resolved.amount,
      method: resolved.method,
      sharedWithPoIds: resolved.sharedWith,
    });
    total = roundMoney(total + resolved.amount);
  }

  return { total, attributions };
}

export function planPoPurchasedAmount(input: {
  purchaseRequisitionId: string;
  linkedExpenseIds: string[];
  beforePurchasedAmount: number | null;
  chargesById: Map<string, ChargeForPoAttribution> | Record<string, ChargeForPoAttribution>;
  expenseToPoIds?: Map<string, string[]> | Record<string, string[]>;
}): {
  skipReason: 'no_linked_charges' | 'no_valid_charges' | null;
  afterPurchasedAmount: number | null;
  changed: boolean;
  missingExpenseIds: string[];
} {
  const linkedExpenseIds = normalizeLinkedExpenseIds(input.linkedExpenseIds);
  const getCharge = (id: string): ChargeForPoAttribution | undefined => {
    if (input.chargesById instanceof Map) return input.chargesById.get(id);
    return input.chargesById[id];
  };

  if (!linkedExpenseIds.length) {
    return {
      skipReason: 'no_linked_charges',
      afterPurchasedAmount: null,
      changed: false,
      missingExpenseIds: [],
    };
  }

  const missingExpenseIds: string[] = [];
  const charges: ChargeForPoAttribution[] = [];
  for (const id of linkedExpenseIds) {
    const charge = getCharge(id);
    if (!charge) {
      missingExpenseIds.push(id);
      continue;
    }
    charges.push({ ...charge, id });
  }

  if (!charges.length) {
    return {
      skipReason: 'no_valid_charges',
      afterPurchasedAmount: null,
      changed: false,
      missingExpenseIds,
    };
  }

  const attributed = attributeChargesToPurchaseOrder({
    purchaseRequisitionId: input.purchaseRequisitionId,
    charges,
    expenseToPoIds: input.expenseToPoIds,
  });
  const after = attributed.total;
  const before = input.beforePurchasedAmount;
  const changed =
    before == null ? after !== 0 || charges.length > 0 : Math.abs(before - after) > 0.005;

  return {
    skipReason: null,
    afterPurchasedAmount: after,
    changed,
    missingExpenseIds,
  };
}
